import { useEffect, useState } from 'react'
import type { TableNewProps, TableRow as TableRowType } from './types'
import {
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  RowSelectionState,
  SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { TableTitle } from './components/Title/Title'
import { TableRow } from './components/Row'
import { getColumns } from './utils/getColumns'
import { TableHeader } from './components/Header'
import { Pagination } from './components/Pagination'
import { RowsPerPage } from './components/RowsPerPage'
import { DEFAULT_ROWS_PER_PAGE } from './constants'
import { RowsPerPageCount } from './components/RowsPerPage/types'
import clsx from 'clsx'

export function TableNew<T>({
  data,
  columnDefinitions,
  canSelect,
  title,
  pagination,
  rowsPerPage,
  onRowSelect,
  initialRowSelection = {},
  totalRows,
  sort,
  condensed,
  striped,
  onRowClick,
  getRowId,
  rowSelectionLimit,
  rowDisabledTooltip,
}: TableNewProps<T & TableRowType>) {
  const [sorting, setSorting] = useState<SortingState>(sort?.sorting || [])

  const [rowsPerPageCount, setRowsPerPageCount] = useState(
    rowsPerPage?.rowsPerPage || DEFAULT_ROWS_PER_PAGE,
  )
  const [rowSelection, setRowSelection] =
    useState<RowSelectionState>(initialRowSelection)
  const [currentPage, setCurrentPage] = useState(pagination?.currentPageId || 0)
  const selectedRowCount = Object.keys(rowSelection).length

  const columns = getColumns(
    columnDefinitions,
    canSelect,
    selectedRowCount,
    rowSelectionLimit,
    rowDisabledTooltip,
  )
  const rowCountChangeFn =
    typeof rowsPerPage?.onChangeRowCount !== 'undefined'
      ? rowsPerPage.onChangeRowCount
      : setRowsPerPageCount
  const currentPageChangeFn =
    typeof pagination?.onPageChange !== 'undefined'
      ? pagination.onPageChange
      : setCurrentPage

  const table = useReactTable({
    data,
    columns,
    manualSorting: typeof sort !== 'undefined',
    state: {
      sorting: sort?.sorting ?? sorting,
      rowSelection,
    },
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: rowsPerPageCount,
      },
      columnPinning: {
        left: columnDefinitions
          .filter((col) => col.pinning === 'left')
          .map((col) => col.id),
        right: columnDefinitions
          .filter((col) => col.pinning === 'right')
          .map((col) => col.id),
      },
    },
    enableColumnPinning: true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    getRowId,
  })

  const handleRowCountChange = (count: RowsPerPageCount) => {
    rowCountChangeFn(count)
  }

  const pageCount = Math.ceil(totalRows / rowsPerPageCount)

  useEffect(() => {
    currentPageChangeFn(0)
  }, [pageCount])

  useEffect(() => {
    if (typeof sort === 'undefined') return
    sort.onSort(sorting)
  }, [sorting])

  // If we're externally controlling the current page
  useEffect(() => {
    if (typeof pagination?.currentPageId !== 'undefined')
      setCurrentPage(pagination.currentPageId)
    if (typeof rowsPerPage?.rowsPerPage !== 'undefined')
      setRowsPerPageCount(rowsPerPage.rowsPerPage)
  }, [pagination?.currentPageId, rowsPerPage?.rowsPerPage])

  const handlePageChange = (pageId: number) => {
    currentPageChangeFn(pageId)
  }

  useEffect(() => {
    if (rowsPerPage?.rowsPerPage) table.setPageSize(rowsPerPage.rowsPerPage)
  }, [rowsPerPage?.rowsPerPage])

  useEffect(() => {
    const selectedIds = Object.keys(rowSelection).map((v) => v)
    if (onRowSelect) onRowSelect(selectedIds)
  }, [rowSelection])

  const hasPagination =
    (pagination && pageCount > 1) ||
    (rowsPerPage && rowsPerPage.onChangeRowCount)

  const hasTitle = !!title

  return (
    <div className="flex flex-col">
      {hasTitle && (
        <div className="overflow-hidden rounded-t-xl border border-b-0 border-content-neutral-subtle">
          <TableTitle {...title} />
        </div>
      )}
      <div
        className={clsx(
          'overflow-x-auto border border-content-neutral-subtle',
          { 'rounded-b-xl': !hasPagination, 'rounded-t-xl': !hasTitle },
        )}
      >
        <table className="w-full">
          <TableHeader
            table={table}
            sortingState={sorting}
            columnDefinitions={columnDefinitions}
            striped={striped}
            condensed={condensed}
          />
          <tbody>
            {table.getRowModel().rows.map((row, index, rows) => (
              <TableRow<T>
                key={row.id}
                row={row}
                isCondensed={condensed}
                onRowClick={onRowClick}
                isStriped={striped ? index % 2 === 0 : false}
                isLast={index === rows.length - 1}
              />
            ))}
          </tbody>
        </table>
      </div>
      {hasPagination && (
        <div className="flex justify-between space-x-5 rounded-b-xl border border-t-0 border-content-neutral-subtle bg-white p-4">
          {pagination && pageCount > 1 ? (
            <Pagination
              {...pagination}
              pageCount={pageCount}
              currentPageId={currentPage}
              onPageChange={handlePageChange}
              condensed={condensed}
            />
          ) : (
            <div />
          )}
          {rowsPerPage && rowsPerPage.onChangeRowCount ? (
            <RowsPerPage
              {...rowsPerPage}
              onChangeRowCount={handleRowCountChange}
              rowsPerPage={rowsPerPageCount}
            />
          ) : (
            <div />
          )}
        </div>
      )}
    </div>
  )
}
